/**
 * Contact Page
 */

import React, { useState } from 'react';
import { Mail, Send, CheckCircle, MessageCircle, Clock } from 'lucide-react';

const SUPPORT_EMAIL = 'support@khaltihaila.com';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate submission - in production, connect to backend/email service
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1500);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (submitted) {
    return (
      <div className="pt-20 min-h-screen bg-gradient-to-br from-rose-50 to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">تم إرسال رسالتك! ✅</h2>
          <p className="text-gray-600 mb-6">
            شكراً لتواصلك معنا، راح نرد عليك بأسرع وقت ممكن
          </p>
          <a
            href="#/"
            className="inline-block px-8 py-3 bg-gradient-to-r from-rose-500 to-purple-600 text-white rounded-full font-semibold hover:shadow-lg transition-all"
          >
            العودة للرئيسية
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-gradient-to-br from-rose-50 via-purple-50 to-pink-50 py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <MessageCircle className="w-16 h-16 text-purple-600 mx-auto mb-4" />
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            تواصل معنا
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            عندك سؤال أو اقتراح؟ نحب نسمع منك!
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                كيف نقدر نساعدك؟
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                سواء عندك سؤال عن المنصة، أو تبي تقترح ميزة جديدة، أو حتى تبي تقول لنا رأيك - نحب نسمع منك!
              </p>

              {/* Contact Methods */}
              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-rose-50 rounded-xl p-4">
                  <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-rose-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">البريد الإلكتروني</h3>
                    <a href={`mailto:${SUPPORT_EMAIL}`} className="text-rose-600 hover:underline">
                      {SUPPORT_EMAIL}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-purple-50 rounded-xl p-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">وقت الرد</h3>
                    <p className="text-gray-600">عادةً خلال 24-48 ساعة</p>
                  </div>
                </div>
              </div>

              {/* FAQ Link */}
              <div className="mt-8 bg-gray-50 rounded-xl p-6">
                <h3 className="font-bold text-gray-900 mb-2">💡 نصيحة</h3>
                <p className="text-gray-600 mb-4">
                  قبل ما تتواصل معنا، ممكن تلاقي جوابك في الأسئلة الشائعة
                </p>
                <a
                  href="#/faq"
                  className="text-rose-600 font-medium hover:underline"
                >
                  اطّلع على الأسئلة الشائعة ←
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gradient-to-br from-gray-50 to-rose-50/30 rounded-2xl p-6 md:p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                أرسل لنا رسالة
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    الاسم
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none transition-all"
                    placeholder="اسمك"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    البريد الإلكتروني
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none transition-all"
                    placeholder="email@example.com"
                    dir="ltr"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    الموضوع
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none transition-all bg-white"
                  >
                    <option value="">اختر الموضوع</option>
                    <option value="question">سؤال عام</option>
                    <option value="suggestion">اقتراح ميزة</option>
                    <option value="bug">مشكلة تقنية</option>
                    <option value="account">مشكلة في الحساب</option>
                    <option value="other">أخرى</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    الرسالة
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none transition-all resize-none"
                    placeholder="اكتب رسالتك هنا..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-rose-500 to-purple-600 text-white rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      إرسال
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
